// Configuración → Correos (§1.3): lo que llega a la casilla de contacto de la web.
// Lista de la maqueta (.mail); el correo se abre en un cajón y al abrirlo queda leído.
import React, { useEffect, useState } from 'react';
import { Mail, RefreshCcw, Trash2 } from 'lucide-react';
import { EmailMessage } from '../types';
import { adminFetch } from '../api';
import { Alert, Button, Drawer, Empty, ICON, Skeleton, cx, useToast } from '../admin/ui';

// El HTML de correos externos se muestra aislado: iframe sin scripts y con CSP restrictiva.
function emailSrcDoc(html: string): string {
  const csp = "default-src 'none'; img-src https: data:; style-src 'unsafe-inline'; font-src https: data:";
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank"></head><body style="margin:0;font-family:sans-serif">${html}</body></html>`;
}

const TZ = 'America/Argentina/Buenos_Aires';
const dayKey = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: TZ });

/** «09:12» si es de hoy, «ayer», o «29 oct». */
function shortTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (dayKey(d) === dayKey(now)) {
    return d.toLocaleTimeString('es-AR', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false });
  }
  if (dayKey(d) === dayKey(new Date(now.getTime() - 86400000))) return 'ayer';
  return d.toLocaleDateString('es-AR', { timeZone: TZ, day: 'numeric', month: 'short' }).replace('.', '');
}

function preview(email: EmailMessage): string {
  const raw = email.text || (email.html || '').replace(/<style[\s\S]*?<\/style>/gi, ' ').replace(/<[^>]+>/g, ' ');
  return raw.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
}

export function EmailInbox({ onUnreadChange }: { onUnreadChange?: (unread: number) => void } = {}) {
  const toast = useToast();
  const [emails, setEmails] = useState<EmailMessage[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch('/api/emails');
      if (!res.ok) throw new Error(`El servidor respondió ${res.status}.`);
      const data: EmailMessage[] = await res.json();
      data.sort((a, b) => (b.receivedAt || '').localeCompare(a.receivedAt || ''));
      setEmails(data);
    } catch (err: any) {
      setError(err?.message || 'Sin conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  useEffect(() => {
    if (emails) onUnreadChange?.(emails.filter((e) => !e.read).length);
  }, [emails]);

  // Esperar 4 s a la segunda pulsación de «Borrar»
  useEffect(() => {
    if (!confirmDelete) return;
    const t = window.setTimeout(() => setConfirmDelete(false), 4000);
    return () => window.clearTimeout(t);
  }, [confirmDelete]);

  const open = emails?.find((e) => e.id === openId) || null;

  const openEmail = async (email: EmailMessage) => {
    setOpenId(email.id!);
    setConfirmDelete(false);
    if (email.read) return;
    try {
      const res = await adminFetch(`/api/emails/${email.id}/read`, { method: 'POST' });
      if (!res.ok) throw new Error(`El servidor respondió ${res.status}.`);
      setEmails((prev) => prev && prev.map((e) => (e.id === email.id ? { ...e, read: true } : e)));
    } catch (err: any) {
      toast.show(`No se pudo marcar como leído: ${err?.message || err}`, 'error');
    }
  };

  const deleteOpen = async () => {
    if (!open) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      const res = await adminFetch(`/api/emails/${open.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`El servidor respondió ${res.status}.`);
      setEmails((prev) => prev && prev.filter((e) => e.id !== open.id));
      setOpenId(null);
      toast.show('Correo borrado');
    } catch (err: any) {
      toast.show(`No se borró el correo: ${err?.message || err}`, 'error');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  let body: React.ReactNode;
  if (!emails && !error) {
    body = (
      <div className="flex flex-col gap-1.5" aria-busy="true">
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
      </div>
    );
  } else if (!emails) {
    body = (
      <Alert tone="red" action={<Button size="sm" onClick={fetchEmails}>Reintentar</Button>}>
        No se pudieron cargar los correos. {error}
      </Alert>
    );
  } else if (emails.length === 0) {
    body = (
      <Empty icon={Mail} title="Sin correos">
        Lo que llegue a la casilla de contacto de la web aparece acá.
      </Empty>
    );
  } else {
    body = (
      <>
        {error && <Alert tone="amber">No se pudo actualizar la bandeja: {error}</Alert>}
        <div className="overflow-hidden rounded-[14px] border border-adm-border bg-adm-surface">
          {emails.map((email) => (
            <button
              key={email.id}
              type="button"
              onClick={() => openEmail(email)}
              className="flex w-full cursor-pointer items-start gap-3.5 border-b border-adm-border bg-transparent px-4 py-3.5 text-left transition-colors last:border-b-0 hover:bg-adm-t5"
            >
              <span className={cx('mt-[7px] size-2 shrink-0 rounded-full', !email.read && 'bg-adm-acento')} aria-hidden="true" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className={cx('truncate text-sm text-adm-text', !email.read && 'font-semibold')}>
                  {email.subject || '(Sin asunto)'}
                  {!email.read && <span className="sr-only"> · sin leer</span>}
                </span>
                <span className="truncate text-[13px] text-adm-t55">
                  {email.from} · {preview(email)}
                </span>
              </span>
              <span className="whitespace-nowrap text-xs text-adm-t55">{shortTime(email.receivedAt)}</span>
            </button>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" icon={<RefreshCcw {...ICON} />} loading={loading && !!emails} onClick={fetchEmails}>
          Actualizar
        </Button>
      </div>
      {body}

      <Drawer
        open={open !== null}
        onClose={() => setOpenId(null)}
        eyebrow="Correo"
        title={open?.subject || '(Sin asunto)'}
        subtitle={open ? `De ${open.from} · ${new Date(open.receivedAt).toLocaleString('es-AR', { timeZone: TZ })}` : undefined}
        footer={
          <>
            <Button
              variant={confirmDelete ? 'danger' : 'ghost'}
              tone={confirmDelete ? undefined : 'danger'}
              icon={<Trash2 {...ICON} />}
              loading={deleting}
              onClick={deleteOpen}
            >
              {confirmDelete ? 'Tocá de nuevo para borrar' : 'Borrar'}
            </Button>
            <span className="flex-1" />
            <Button variant="primary" onClick={() => setOpenId(null)}>
              Listo
            </Button>
          </>
        }
      >
        {open && (
          <>
            <p className="text-[13px] text-adm-t55">Para: {(open.to || []).join(', ')}</p>
            {open.html ? (
              <iframe
                title={`Correo: ${open.subject}`}
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                srcDoc={emailSrcDoc(open.html)}
                className="h-[60vh] w-full rounded-xl border border-adm-border bg-white"
              />
            ) : (
              <div className="whitespace-pre-wrap rounded-xl border border-adm-border bg-adm-card p-4 text-sm text-adm-t70">
                {open.text || <span className="italic text-adm-t40">Sin contenido</span>}
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
